import { FC, ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerInfoBox } from '../../components/custom_components/InfoBox'
import { InfoBoxTile } from '../../components/custom_components/InfoBox/InfoBoxTileTemplate/InfoBoxTile'
import { PerInfoBoxNumberElement } from '../../components/custom_components/InfoBox/InfoBoxNumberElement'
import PerButtonCircle from '../../components/custom_components/ButtonCircle'
import PerFlexRow from '../../components/custom_components/FlexRow'
import { PerInfoBoxButtonGroupElement } from '../../components/custom_components/InfoBox/InfoBoxButtonGroup'
import { buttonGroupButton } from '../../components/custom_components/ButtonGroup'
import { Col, Select, Icon, Divider } from 'antd'
import PerText from '../../components/custom_components/Text'
import PerButton from '../../components/custom_components/Button'
import { EmployeeInfoBoxFilter, noFilterOnEmployeesApplied } from './util'
import { EmployeeLevelEnum, CareerPathEvaluationState, SkillEmployeeStatusEnum } from '../../types'
import { getSortOrderByEmployeeLevel } from '../companySettings/employees/employeesLevelSorterUtil'
import {
  refreshSubordinatesOfCompanySkillStatuses,
  refreshSubordinatesSkillStatuses,
} from '../../graphql/skill-status/useSkillStatusMutations'
import { useDateTime } from '../../shared/useDateTime'
import { useAuth } from '../../context/auth-context'
import {
  SkillStatusOutdated,
  SkillStatusCompleted,
  SkillStatusMissing,
  SkillStatusCvIncompleteOrNearlyOutdated,
} from '../skillStatus/SkillStatusLabelComponent'
import { TFunction } from 'i18next'
import { showNotification } from '../../components/custom_components/Notification/notification'

const { Option } = Select

type EmployeeInfoBoxProps = {
  filterResultLength: number
  totalOfEmployees: number
  hasCareerPathData: boolean
  modalFilterActive: boolean
  resetTableData: () => void
  setModalOpen: (values: boolean) => void
  swichContentButtons?: buttonGroupButton[]
  toggleMonetaryButton?: ReactNode
  infoBoxFilter: EmployeeInfoBoxFilter
  setInfoBoxFilter: (value: EmployeeInfoBoxFilter) => void
  possibleLevels: EmployeeLevelEnum[]
  possibleBUs: string[]
  employeeLevels: EmployeeLevelEnum[]
  atLeastOneExpiredOrUnknownSkillStatus: boolean
  isCompanySettings: boolean
  companyId: string
  isReviewer?: boolean
  buId?: string | null
  oldestSyncUTCDate?: string
}

export const EmployeeInfoBox: FC<EmployeeInfoBoxProps> = ({
  filterResultLength,
  totalOfEmployees,
  hasCareerPathData,
  resetTableData,
  setModalOpen,
  swichContentButtons,
  toggleMonetaryButton,
  infoBoxFilter,
  setInfoBoxFilter,
  modalFilterActive,
  possibleLevels,
  possibleBUs,
  employeeLevels,
  atLeastOneExpiredOrUnknownSkillStatus,
  isCompanySettings,
  companyId,
  isReviewer,
  buId,
  oldestSyncUTCDate,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { formatDateTimeShort } = useDateTime(user)
  const clearButtonDisabled = noFilterOnEmployeesApplied(infoBoxFilter, modalFilterActive)
  const [isRefreshMySkillStatusProcessing, toggleRefreshMySkillStatusProcessing] = useState<boolean>(false)
  const [skillStatusError, setSkillStatusError] = useState<boolean>(false)

  const buildSemaphoreText = (t: TFunction, color: string) => {
    return t(`profile.skillCenter.public.label.filters.${color}`)
  }

  return (
    <>
      <PerInfoBox>
        <PerFlexRow align="middle" justify="space-between" width="fullWidth" className="fullHeight">
          <Col span={8} className="fullHeight">
            <PerFlexRow direction="column" justify="space-between" className="fullHeight" width="fullWidth">
              <PerText mb="xxs" appearance="tile-heading">
                {t('companySettingsView.appraisalTab.filter.heading')}
              </PerText>

              <PerFlexRow align="middle" justify="space-between" width="fullWidth" className="fullHeight">
                <Col span={12} className="fullHeight">
                  <PerFlexRow direction="column" justify="space-between" width="fullWidth" className="fullHeight">
                    <PerFlexRow>
                      <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxxs">
                        {t('employeeList.infoBox.filters.level')}
                      </PerText>
                      <Select
                        allowClear
                        onChange={(value: EmployeeLevelEnum) => {
                          setInfoBoxFilter({ ...infoBoxFilter, employeeLevel: value })
                        }}
                        value={infoBoxFilter.employeeLevel}
                        placeholder={t('employeeList.infoBox.filters.levelPlaceholder')}
                        style={{ width: '100%' }}
                      >
                        {possibleLevels
                          .sort((levelA: EmployeeLevelEnum, levelB: EmployeeLevelEnum) => {
                            return getSortOrderByEmployeeLevel(levelA, employeeLevels) - getSortOrderByEmployeeLevel(levelB, employeeLevels)
                          })
                          .map((level) => (
                            <Option key={level} value={level}>
                              {level}
                            </Option>
                          ))}
                      </Select>
                    </PerFlexRow>
                    <PerFlexRow>
                      <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxxs">
                        {t('employeeList.infoBox.filters.bu')}
                      </PerText>
                      <Select
                        allowClear
                        onChange={(value: string) => {
                          setInfoBoxFilter({ ...infoBoxFilter, bu: value })
                        }}
                        value={infoBoxFilter.bu}
                        placeholder={t('employeeList.infoBox.filters.buPlaceholder')}
                        style={{ width: '100%' }}
                      >
                        {possibleBUs
                          .sort((buA: string, buB: string) => {
                            return buA.localeCompare(buB)
                          })
                          .map((bu) => (
                            <Option key={bu} value={bu}>
                              {bu}
                            </Option>
                          ))}
                      </Select>
                    </PerFlexRow>
                  </PerFlexRow>
                </Col>

                <Col span={12} className="fullHeight">
                  <PerFlexRow direction="column" justify="space-between" className="fullHeight">
                    <PerFlexRow>
                      <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxxs">
                        {t('employeeList.infoBox.filters.skillStatus')}
                      </PerText>
                      <Select
                        allowClear
                        onChange={(value: SkillEmployeeStatusEnum) => {
                          setInfoBoxFilter({ ...infoBoxFilter, skillStatus: value })
                        }}
                        value={infoBoxFilter.skillStatus}
                        placeholder={t('employeeList.infoBox.filters.skillStatusPlaceholder')}
                        style={{ width: '100%' }}
                      >
                        <Option
                          key={SkillEmployeeStatusEnum.Completed}
                          value={SkillEmployeeStatusEnum.Completed}
                          title={buildSemaphoreText(t, 'green')}
                        >
                          <PerFlexRow align="middle" className="overflow--hidden">
                            <SkillStatusCompleted size={20} />
                            <div style={{ maxWidth: '80%' }}>
                              <PerText ml="xxxs" fontSize="s" className="truncate-text">
                                {buildSemaphoreText(t, 'green')}
                              </PerText>
                            </div>
                          </PerFlexRow>
                        </Option>
                        <Option
                          key={SkillEmployeeStatusEnum.CvIncomplete}
                          value={SkillEmployeeStatusEnum.CvIncomplete}
                          title={buildSemaphoreText(t, 'yellow')}
                        >
                          <PerFlexRow align="middle" className="overflow--hidden">
                            <SkillStatusCvIncompleteOrNearlyOutdated size={20} />
                            <div style={{ maxWidth: '80%' }}>
                              <PerText ml="xxxs" fontSize="s" className="truncate-text">
                                {buildSemaphoreText(t, 'yellow')}
                              </PerText>
                            </div>
                          </PerFlexRow>
                        </Option>
                        <Option
                          key={SkillEmployeeStatusEnum.Outdated}
                          value={SkillEmployeeStatusEnum.Outdated}
                          title={buildSemaphoreText(t, 'red')}
                        >
                          <PerFlexRow align="middle" className="overflow--hidden">
                            <SkillStatusOutdated size={20} />
                            <div style={{ maxWidth: '80%' }}>
                              <PerText ml="xxxs" fontSize="s" className="truncate-text">
                                {buildSemaphoreText(t, 'red')}
                              </PerText>
                            </div>
                          </PerFlexRow>
                        </Option>
                        <Option
                          key={SkillEmployeeStatusEnum.Missing}
                          value={SkillEmployeeStatusEnum.Missing}
                          title={buildSemaphoreText(t, 'missing')}
                        >
                          <PerFlexRow align="middle" className="overflow--hidden">
                            <SkillStatusMissing size={20} />
                            <div style={{ maxWidth: '80%' }}>
                              <PerText ml="xxxs" fontSize="s" className="truncate-text">
                                {buildSemaphoreText(t, 'missing')}
                              </PerText>
                            </div>
                          </PerFlexRow>
                        </Option>
                      </Select>
                    </PerFlexRow>
                    {hasCareerPathData && (
                      <PerFlexRow>
                        <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxxs">
                          {t('employeeList.infoBox.filters.careerPath')}
                        </PerText>
                        <Select
                          allowClear
                          onChange={(value: CareerPathEvaluationState) => {
                            setInfoBoxFilter({ ...infoBoxFilter, careerPath: value })
                          }}
                          value={infoBoxFilter.careerPath}
                          placeholder={t('employeeList.infoBox.filters.careerPathPlaceholder')}
                          style={{ width: '100%' }}
                        >
                          <Option key={CareerPathEvaluationState.Inline} value={CareerPathEvaluationState.Inline}>
                            {t('employeeList.infoBox.filters.inline')}
                          </Option>
                          <Option key={CareerPathEvaluationState.OutOfLine} value={CareerPathEvaluationState.OutOfLine}>
                            {t('employeeList.infoBox.filters.outOfLine')}
                          </Option>
                        </Select>
                      </PerFlexRow>
                    )}
                  </PerFlexRow>
                </Col>
              </PerFlexRow>
            </PerFlexRow>
          </Col>

          <Divider type="vertical" className="fullHeight" />

          <InfoBoxTile
            heading={t('companySettingsView.appraisalTab.filter.counters')}
            contentFlex="fixed"
            headingAlign="center"
            contentAlign="end"
            childrenFlexDirection="column"
          >
            <PerFlexRow direction="column" noWrap width="fullWidth">
              <PerInfoBoxNumberElement numberFontSize="xxl" outOfFontSize="l" number={filterResultLength} outOf={`${totalOfEmployees}`} />
              <PerFlexRow justify="space-between" align="middle" mt="xxs">
                <PerButton
                  size="medium"
                  text={t('employeeList.infoBox.filters.filter')}
                  color={modalFilterActive ? 'warning' : 'success'}
                  type="button"
                  variant="pastel"
                  before={modalFilterActive ? 'edit_fill' : 'add_stroke'}
                  onClick={() => setModalOpen(true)}
                  tooltip={modalFilterActive ? t('employeeList.infoBox.filters.editFilters') : t('employeeList.infoBox.filters.addFilters')}
                />
                <PerButton
                  size="medium"
                  text={t('employeeList.infoBox.filters.clear')}
                  color="error"
                  type="button"
                  variant="pastel"
                  before="delete_fill"
                  onClick={() => resetTableData()}
                  disabled={clearButtonDisabled}
                  tooltip={
                    clearButtonDisabled ? t('employeeList.infoBox.filters.noActiveFilters') : t('employeeList.infoBox.filters.clearFilters')
                  }
                />
              </PerFlexRow>
            </PerFlexRow>
          </InfoBoxTile>

          <Divider type="vertical" className="fullHeight" />
          <Col span={4} className="fullHeight">
            <InfoBoxTile heading={t('employeeList.infoBox.skillStatus')} contentFlex="fixed" headingAlign="center">
              <PerFlexRow direction="column" align="middle" justify="space-between" className="fullHeight">
                <PerFlexRow justify="center">
                  <PerText fontSize="xs" color="secondary" appearance="quote" align="center">
                    {oldestSyncUTCDate
                      ? t('employeeList.infoBox.skillStatusDescription', { date: formatDateTimeShort(oldestSyncUTCDate) })
                      : t('employeeList.infoBox.skillStatusDescription2')}
                  </PerText>
                </PerFlexRow>
                <PerButtonCircle
                  tooltip={
                    skillStatusError
                      ? t('employeeList.infoBox.refreshError')
                      : !atLeastOneExpiredOrUnknownSkillStatus
                      ? t('employeeList.infoBox.noRefreshRequired')
                      : t('profile.skillCenter.public.refreshStatusButton')
                  }
                  color={skillStatusError ? 'error' : 'primary'}
                  variant="pastel"
                  size="large"
                  icon={skillStatusError ? 'warning_fill' : !atLeastOneExpiredOrUnknownSkillStatus ? 'check_stroke' : undefined}
                  onClick={async () => {
                    toggleRefreshMySkillStatusProcessing(true)
                    const { data: skillStatusData, errors: skillStatusErrors } = isCompanySettings
                      ? await refreshSubordinatesOfCompanySkillStatuses(companyId)
                      : await refreshSubordinatesSkillStatuses(companyId, !!isReviewer, buId)
                    if (!skillStatusData && skillStatusErrors) {
                      showNotification({
                        type: 'error',
                        message: t('employeeList.infoBox.notifications.skillStatusSync.error.title'),
                        description: t('employeeList.infoBox.notifications.skillStatusSync.error.description'),
                      })
                      setSkillStatusError(true)
                    } else {
                      showNotification({
                        type: 'success',
                        message: t('employeeList.infoBox.notifications.skillStatusSync.success.title'),
                        description: t('employeeList.infoBox.notifications.skillStatusSync.success.description'),
                      })
                    }
                    toggleRefreshMySkillStatusProcessing(false)
                  }}
                  isProcessing={{ active: isRefreshMySkillStatusProcessing }}
                  disabled={!atLeastOneExpiredOrUnknownSkillStatus || skillStatusError}
                  customIconComponent={
                    skillStatusError || !atLeastOneExpiredOrUnknownSkillStatus ? undefined : <Icon type="reload" style={{ fontSize: 16 }} />
                  }
                />
              </PerFlexRow>
            </InfoBoxTile>
          </Col>

          {toggleMonetaryButton && (
            <>
              <Divider type="vertical" className="fullHeight" />
              <Col span={5} className="fullHeight">
                <InfoBoxTile heading={t('employeeList.infoBox.toggleMonetary.heading')} contentFlex="fixed" headingAlign="center">
                  <PerFlexRow direction="column" align="middle" justify="space-between" className="fullHeight">
                    <PerText fontSize="xs" color="secondary" appearance="quote" align="center">
                      {t('employeeList.infoBox.monetaryVisibilityDescription')}
                    </PerText>
                    {toggleMonetaryButton}
                  </PerFlexRow>
                </InfoBoxTile>
              </Col>
            </>
          )}
          {swichContentButtons && (
            <>
              <Divider type="vertical" className="fullHeight" />
              <Col span={5} className="fullHeight">
                <InfoBoxTile heading={t('employeeList.infoBox.switchContent')} contentFlex="fixed" headingAlign="center">
                  <PerFlexRow direction="column" align="middle" justify="space-between" className="fullHeight">
                    <PerText fontSize="xs" color="secondary" appearance="quote" align="center">
                      {t('employeeList.infoBox.switchContentDescription')}
                    </PerText>
                    <PerInfoBoxButtonGroupElement buttons={swichContentButtons} />
                  </PerFlexRow>
                </InfoBoxTile>
              </Col>
            </>
          )}
        </PerFlexRow>
      </PerInfoBox>
    </>
  )
}
